/**
 * ProtectedRoute Component
 *
 * Wraps routes that require authentication.
 * Redirects to home page if user is not logged in, where the MMT header
 * provides login functionality.
 *
 * Dispatches initSession on mount to restore an existing session from
 * sessionStorage or URL, and fetch a JWT token from the backend.
 * Does NOT create a new session — that happens when the user sends
 * their first message (via createSessionThunk).
 */

import { type ReactNode, useEffect } from 'react';
import { useAppSelector, useAppDispatch } from '@/store/hooks';
import {
  selectIsLoggedIn,
  selectIsAuthChecked,
  selectIsSessionReady,
  initSession,
} from '@/features/auth/redux';
import { ROUTES } from '@/shared/constants';

interface ProtectedRouteProps {
  readonly children: ReactNode;
}

export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const dispatch = useAppDispatch();
  const isLoggedIn = useAppSelector(selectIsLoggedIn);
  const isAuthChecked = useAppSelector(selectIsAuthChecked);
  const isSessionReady = useAppSelector(selectIsSessionReady);

  // Restore existing session (from storage or URL) when user is logged in.
  // If no session exists, initSession returns empty — page renders blank (new chat).
  useEffect(() => {
    if (!isAuthChecked || !isLoggedIn || isSessionReady) return;
    void dispatch(initSession());
  }, [isAuthChecked, isLoggedIn, isSessionReady, dispatch]);

  // Redirect to home when not logged in — MMT header there handles login
  useEffect(() => {
    if (isAuthChecked && !isLoggedIn) {
      window.location.href = ROUTES.HOME;
    }
  }, [isAuthChecked, isLoggedIn]);

  // Loading: auth check in progress
  if (!isAuthChecked) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <div className="border-primary mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-b-2" />
          <p className="text-medium-emphasis">Verifying authentication...</p>
        </div>
      </div>
    );
  }

  // Not logged in — redirecting to home
  if (!isLoggedIn) {
    return null;
  }

  // Wait for session restore + JWT token fetch to complete
  if (!isSessionReady) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <div className="border-primary mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-b-2" />
          <p className="text-medium-emphasis">Loading...</p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
